import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Activity, ShieldCheck, GitBranch, Layers, Eye } from "lucide-react";
import EquilibriumLayout from "@/components/equilibrium/EquilibriumLayout";
import { fetchEqModules } from "@/lib/eq";

const ICONS = {
  spyglass: Eye,
  atlasbridge: GitBranch,
  horizon: Activity,
  dutydeck: ShieldCheck,
  oli: Layers,
};

const PRINCIPLES = [
  { code: "P.01", title: "Entropy reduction", desc: "We reduce the noise the system injects into its own truth." },
  { code: "P.02", title: "Signal coherence", desc: "Conflicting telemetry resolved into a single causal story." },
  { code: "P.03", title: "Provenance verification", desc: "Every assertion traceable to the event that produced it." },
  { code: "P.04", title: "Operational causality", desc: "Symptoms tracked back to the operating decision that caused them." },
  { code: "P.05", title: "Reconciliation", desc: "Finance, ops, and integration views forced into one ledger." },
  { code: "P.06", title: "Intervention timing", desc: "The window between detection and damage is the asset." },
  { code: "P.07", title: "Trust preservation", desc: "Coherent operational truth long enough for accountability to matter." },
];

export default function EquilibriumHome() {
  const [modules, setModules] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEqModules().then(setModules).catch(() => setModules([]));
  }, []);

  return (
    <EquilibriumLayout>
      {/* HERO */}
      <section
        data-testid="eq-hero"
        className="relative min-h-[88vh] flex items-center overflow-hidden pb-20"
      >
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute inset-0 bg-radial-fade" />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <span className="signal-dot animate-pulse-signal" />
              <span className="eyebrow">EQUILIBRIUM-AI / OPERATIONAL.INTELLIGENCE / v0.42</span>
            </div>
            <h1 className="font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-[68px] leading-[1.04]">
              Coherent operational <br />
              <span className="text-axiom-text">truth, long enough for</span> <br />
              <span className="text-axiom-accent">intervention.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-axiom-muted leading-relaxed">
              Equilibrium-AI is a modular intelligence ecosystem for supply chains that operate in
              noisy environments, fragmented truth, and continuous entropy injection. The platform
              maintains coherence long enough for recovery, accountability, and liquidity confidence
              to remain possible.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="eq-hero-cta-diag"
                onClick={() => navigate("/equilibrium/diagnostic")}
                className="btn-axiom"
              >
                Run Operator Diagnostic <ArrowRight size={16} />
              </button>
              <button
                data-testid="eq-hero-cta-console"
                onClick={() => navigate("/equilibrium/console")}
                className="btn-axiom-outline"
              >
                Enter Operator Console
              </button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "5", v: "Modular services" },
                { k: "24/7", v: "Provenance ledger" },
                { k: "0", v: "Untraceable assertions" },
              ].map((s) => (
                <div key={s.v} className="border-t border-white/10 pt-3">
                  <div className="font-display text-2xl text-axiom-text">{s.k}</div>
                  <div className="num-label mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Live telemetry panel */}
          <div className="lg:col-span-5">
            <div className="relative border border-white/10 bg-axiom-surface/40 p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="num-label">SYSTEM TELEMETRY · LIVE</span>
                <span className="signal-dot animate-pulse-signal" style={{ width: 6, height: 6 }} />
              </div>

              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                {[
                  { k: "COHERENCE", v: "93.8%", accent: true },
                  { k: "ENTROPY Δ", v: "0.14" },
                  { k: "PROVENANCE", v: "APPEND-ONLY" },
                  { k: "INTERVENTIONS", v: "2 OPEN" },
                ].map((m) => (
                  <div key={m.k} className="bg-axiom-bg p-4">
                    <div className="num-label">{m.k}</div>
                    <div
                      className={`mt-2 font-display text-2xl ${
                        m.accent ? "text-axiom-accent" : "text-axiom-text"
                      }`}
                    >
                      {m.v}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <div className="num-label mb-2">MODULE STATUS</div>
                <div className="space-y-2">
                  {modules.map((m) => {
                    const Icon = ICONS[m.key] || Activity;
                    return (
                      <Link
                        key={m.key}
                        to={`/equilibrium/modules/${m.key}`}
                        className="flex items-center gap-3 border border-white/10 hover:border-axiom-accent/40 px-3 py-2 transition-colors"
                        data-testid={`eq-hero-mod-${m.key}`}
                      >
                        <Icon size={14} className="text-axiom-accent" strokeWidth={1.5} />
                        <span className="font-mono text-xs text-axiom-text uppercase tracking-wider">
                          {m.name}
                        </span>
                        <span className="num-label">{m.version}</span>
                        <span className="flex-1" />
                        <StatusPill status={m.status} />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section
        data-testid="eq-principles"
        className="relative py-24 border-t border-white/5"
      >
        <div className="absolute inset-0 bg-grid-fine opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="num-label">SECTION 02 / FOUNDATIONS</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl text-axiom-text font-bold tracking-tight leading-tight">
                The operating <br />
                <span className="text-axiom-muted">principles beneath</span> <br />
                <span className="text-axiom-accent">the platform.</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-axiom-muted text-base sm:text-lg leading-relaxed">
                The platform's reasoning is not built on dashboards. It is built on seven operating
                principles that determine when a signal is real, when an intervention is owed,
                and when financial exposure begins to accrue.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {PRINCIPLES.map((p) => (
              <div key={p.code} className="bg-axiom-bg p-5" data-testid={`eq-principle-${p.code}`}>
                <div className="flex items-center justify-between">
                  <span className="num-label">{p.code}</span>
                  <span className="signal-dot opacity-40" style={{ width: 6, height: 6 }} />
                </div>
                <div className="mt-6 font-display text-lg text-axiom-text font-bold leading-snug">
                  {p.title}
                </div>
                <p className="mt-2 text-sm text-axiom-muted leading-relaxed">{p.desc}</p>
              </div>
            ))}
            {/* Filler cell to keep grid aligned */}
            <div className="bg-axiom-bg p-5 flex items-end">
              <div className="text-xs font-mono text-axiom-dim leading-relaxed">
                "Equilibrium-AI does not aim for perfect data. It aims for coherent enough truth,
                early enough, that intervention is still cheaper than failure."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section
        data-testid="eq-modules"
        className="relative py-24 border-t border-white/5"
      >
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 items-end">
            <div className="lg:col-span-6">
              <div className="num-label">SECTION 03 / MODULES</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl text-axiom-text font-bold tracking-tight leading-tight">
                Five modules. <br />
                <span className="text-axiom-muted">One coherent system.</span>
              </h2>
            </div>
            <div className="lg:col-span-6">
              <p className="text-axiom-muted text-base leading-relaxed">
                Each module is independently deployable and provenance-aware. They share a
                canonical event substrate so signal observed by SpyGlass arrives in DutyDeck
                with its full lineage intact.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {modules.map((m) => {
              const Icon = ICONS[m.key] || Activity;
              return (
                <Link
                  key={m.key}
                  to={`/equilibrium/modules/${m.key}`}
                  data-testid={`eq-modcard-${m.key}`}
                  className="group bg-axiom-bg p-6 md:p-7 hover:bg-axiom-surface transition-colors flex flex-col"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-10 h-10 border border-white/15 flex items-center justify-center group-hover:border-axiom-accent/60 transition-colors">
                      <Icon size={18} className="text-axiom-accent" strokeWidth={1.4} />
                    </div>
                    <StatusPill status={m.status} />
                  </div>
                  <h3 className="font-display text-2xl text-axiom-text font-bold">
                    {m.name}
                  </h3>
                  <p className="mt-2 text-sm text-axiom-accent/90">{m.tagline}</p>
                  <p className="mt-3 text-sm text-axiom-muted leading-relaxed line-clamp-4">
                    {m.purpose}
                  </p>
                  <div className="mt-auto pt-6 flex items-center gap-4 text-xs font-mono text-axiom-dim">
                    <span>{m.version}</span>
                    <span className="opacity-40">·</span>
                    <span>{m.domain}</span>
                    <span className="flex-1" />
                    <span className="text-axiom-accent">
                      {m.telemetry.coherence_pct}% coh
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="relative py-24 border-t border-white/5">
        <div className="absolute inset-0 bg-radial-fade" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-axiom-text font-bold tracking-tight leading-tight">
            Run the diagnostic. <br />
            <span className="text-axiom-accent">See your own signal.</span>
          </h2>
          <p className="mt-5 text-axiom-muted text-base leading-relaxed">
            Submit an operator-grade diagnostic intake. The platform will compute confidence,
            estimate entropy, and recommend which Equilibrium-AI modules apply to your network.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => navigate("/equilibrium/diagnostic")}
              className="btn-axiom"
              data-testid="eq-bottom-cta-diag"
            >
              Run Operator Diagnostic <ArrowRight size={16} />
            </button>
            <button
              onClick={() => navigate("/equilibrium/console")}
              className="btn-axiom-outline"
            >
              Enter Operator Console
            </button>
          </div>
        </div>
      </section>
    </EquilibriumLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    OPERATIONAL: "border-emerald-400/40 text-emerald-300",
    PROVISIONAL: "border-amber-400/40 text-amber-300",
    FOUNDATIONAL: "border-axiom-accent/40 text-axiom-accent",
  };
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5 ${
        map[status] || "border-white/15 text-axiom-muted"
      }`}
    >
      {status}
    </span>
  );
}
